import type { NavItem } from '@/data/menu';

export function normalizePath(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

export function pathMatches(href: string | undefined, path: string): boolean {
  if (!href || href === '#') return false;
  const target = normalizePath(href);
  const current = normalizePath(path);
  return current === target || (target !== '/' && current.startsWith(`${target}/`));
}

export function itemIsActive(item: NavItem, path: string): boolean {
  return pathMatches(item.href, path) || Boolean(item.children?.some((child) => pathMatches(child.href, path)));
}

export function normalizeText(text: string): string {
  return text.toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function filterMenu(items: NavItem[], query: string): NavItem[] {
  const q = normalizeText(query.trim());
  if (!q) return items;
  return items.flatMap((item) => {
    if (normalizeText(item.label).includes(q)) return [item];
    const children = item.children?.filter((child) => normalizeText(child.label).includes(q));
    return children?.length ? [{ ...item, children }] : [];
  });
}

export function resolveMenuOrder(menu: NavItem[], order: string[]): NavItem[] {
  const remaining = new Map(menu.map((item) => [item.id, item]));
  const result: NavItem[] = [];
  for (const id of order) {
    const item = remaining.get(id);
    if (item) {
      result.push(item);
      remaining.delete(id);
    }
  }
  return [...result, ...remaining.values()];
}

type Crumb = { label: string; href?: string };
const sharedPages: Record<string, string> = {
  '/layout': 'Preferências de layout',
  '/account-settings': 'Minha Conta',
  '/requests': 'Solicitações',
  '/register': 'Cadastro de usuário',
};

export function resolvePageNavigation(menu: NavItem[], path: string): { title: string; crumbs: Crumb[] } | null {
  if (sharedPages[path]) return { title: sharedPages[path], crumbs: [{ label: sharedPages[path] }] };
  let match: { href: string; crumbs: Crumb[] } | undefined;
  for (const item of menu) {
    if (item.href && pathMatches(item.href, path) && (!match || item.href.length > match.href.length)) {
      match = { href: item.href, crumbs: [{ label: item.label, href: item.href }] };
    }
    for (const child of item.children ?? []) {
      if (pathMatches(child.href, path) && (!match || child.href.length > match.href.length)) {
        match = { href: child.href, crumbs: [{ label: item.label }, { label: child.label, href: child.href }] };
      }
    }
  }
  if (!match) return null;
  const suffix = path.slice(match.href.length).split('/').filter(Boolean);
  const action = suffix.some((part) => part === 'novo' || part === 'nova') ? 'Novo'
    : suffix.includes('editar') ? 'Editar' : suffix.length ? 'Visualizar' : '';
  const title = `${match.crumbs[match.crumbs.length - 1].label}${action ? ` · ${action}` : ''}`;
  return { title, crumbs: [...match.crumbs, ...(action ? [{ label: action }] : [])] };
}
